import React from "react";
import styled from "styled-components";
import { Modal, Button, message } from "antd";
import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import QRCode from "react-qr-code";
import QRCodeLib from "qrcode";
import { device } from "../../lib/breakpoints";

/**
 * Share a public URL (application or listing) with copy + QR.
 * Useful for open houses, yard signs, and texting walk-ins.
 */
export function buildApplicationUrl(listingId) {
  if (!listingId) return "";
  const origin = window.location.origin.replace(
    "http://localhost:3000",
    "https://landlordops.com"
  );
  return `${origin}/apply/${listingId}`;
}

export function buildListingShareUrl(listingId) {
  if (!listingId) return "";
  const origin = window.location.origin.replace(
    "http://localhost:3000",
    "https://landlordops.com"
  );
  return `${origin}/lease/${listingId}`;
}

export default function ShareApplicationModal({
  open,
  onClose,
  listingId,
  listingLabel,
  mode = "application",
  onCopied,
  onDownloaded,
}) {
  const isListing = mode === "listing";
  const shareUrl = isListing
    ? buildListingShareUrl(listingId)
    : buildApplicationUrl(listingId);
  const title = isListing ? "Share listing link" : "Share application link";
  const intro = isListing
    ? "Share this link or QR code so prospects can open the listing, chat with Leo, and book tours."
    : "Share this link or QR code so prospects can apply on their phone. No login required.";
  const copySuccess = isListing
    ? "Listing link copied"
    : "Application link copied";
  const downloadPrefix = isListing ? "listing-qr" : "application-qr";

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      message.success(copySuccess);
      onCopied?.(shareUrl);
    } catch {
      message.error("Could not copy link");
    }
  };

  const handleDownloadQR = async () => {
    if (!shareUrl) return;
    try {
      const qrDataUrl = await QRCodeLib.toDataURL(shareUrl, {
        width: 512,
        margin: 2,
        color: { dark: "#000000", light: "#FFFFFF" },
      });
      const link = document.createElement("a");
      const safeName = String(listingLabel || listingId || "listing")
        .replace(/[^\w-]+/g, "_")
        .slice(0, 48);
      link.download = `${downloadPrefix}-${safeName}.png`;
      link.href = qrDataUrl;
      link.click();
      message.success("QR code downloaded");
      onDownloaded?.(shareUrl);
    } catch (error) {
      console.error("Error generating QR:", error);
      message.error("Could not generate QR code");
    }
  };

  return (
    <StyledModal
      title={title}
      open={open}
      onCancel={onClose}
      centered
      width={420}
      destroyOnClose
      footer={[
        <Button
          key="download"
          type="primary"
          icon={
            <DownloadOutlined style={{ filter: "brightness(0) invert(1)" }} />
          }
          onClick={handleDownloadQR}
          disabled={!shareUrl}
        >
          Download QR
        </Button>,
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <Intro>{intro}</Intro>

      <UrlRow>
        <UrlText title={shareUrl}>{shareUrl || "-"}</UrlText>
        <Button
          icon={<CopyOutlined />}
          onClick={handleCopy}
          disabled={!shareUrl}
        >
          Copy
        </Button>
      </UrlRow>

      <QrWrap>
        {shareUrl ? (
          <QRCode
            value={shareUrl}
            size={220}
            style={{ height: "auto", maxWidth: "100%", width: "100%" }}
          />
        ) : null}
      </QrWrap>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  --lo-modal-header-height: 56px;
  --lo-modal-close-size: 32px;

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0;
  }

  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 52px 16px 20px;
    margin: 0;
    min-height: var(--lo-modal-header-height);
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: calc(
      (var(--lo-modal-header-height) - var(--lo-modal-close-size)) / 2
    ) !important;
    right: 16px !important;
    width: var(--lo-modal-close-size) !important;
    height: var(--lo-modal-close-size) !important;
    padding: 0 !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;

    .anticon,
    .anticon svg,
    .ant-modal-close-x,
    .ant-modal-close-icon,
    svg {
      color: white !important;
      fill: white !important;
    }

    &:hover {
      color: white !important;
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: white !important;
      fill: white !important;
    }
  }

  .ant-modal-body {
    padding: 24px;
    background: #ffffff;
  }

  .ant-modal-footer {
    margin: 0;
    padding: 16px 24px 20px;
    border-top: 1px solid #f0f0f0;
    background: #ffffff;
  }

  @media ${device.mobile} {
    --lo-modal-header-height: 48px;
    --lo-modal-close-size: 28px;

    .ant-modal-header {
      padding: 12px 48px 12px 16px;
    }

    .ant-modal-title {
      font-size: 16px;
    }

    .ant-modal-body {
      padding: 16px;
    }

    .ant-modal-footer {
      padding: 12px 16px 16px;
    }
  }
`;

const Intro = styled.p`
  margin: 0 0 1rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--color-text-secondary, #64748b);
`;

const UrlRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1.25rem;
`;

const UrlText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
  font-family: "Monaco", "Courier New", monospace;
  font-weight: 600;
  color: var(--color-primary-dark, #2c5f2d);
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QrWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 1.25rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;
